import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MessageSquare } from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import { useI18n } from '../i18n';
import { chatApi } from '../lib/chat';
import { MemberCard } from './MemberCard';

const MessagingContext = createContext(null);
export const useMessaging = () => useContext(MessagingContext);

export const MessagingProvider = ({ children }) => {
  const { user } = useAuth();
  const uid = user?.user_id;
  const [unread, setUnread] = useState(0);
  const [memberId, setMemberId] = useState(null);
  const refreshUnread = useCallback(async () => {
    if (!uid || document.hidden) return;
    try { const data = await chatApi('/unread'); setUnread(data.count); } catch { /* Keep last confirmed count while offline. */ }
  }, [uid]);
  useEffect(() => {
    setUnread(0); setMemberId(null);
    if (!uid) return;
    refreshUnread();
    const timer = setInterval(refreshUnread, 15000);
    document.addEventListener('visibilitychange', refreshUnread);
    return () => { clearInterval(timer); document.removeEventListener('visibilitychange', refreshUnread); };
  }, [uid, refreshUnread]);
  return <MessagingContext.Provider value={{ unread, refreshUnread, openMember: setMemberId }}>
    {children}
    {uid && <MemberCard key={memberId} userId={memberId} onClose={() => setMemberId(null)} />}
  </MessagingContext.Provider>;
};

export const InboxLink = () => {
  const { user } = useAuth();
  const { unread } = useMessaging();
  const { t } = useI18n();
  if (!user) return null;
  return <Link to="/mensajes" data-testid="inbox-nav-link" aria-label={`${t('chat.title')}${unread ? ` · ${unread} ${t('chat.unread')}` : ''}`} className="relative shrink-0 p-2 text-slate-300 hover:text-amber-400 transition-colors sc-focus rounded-md">
    <MessageSquare size={20} />
    {unread > 0 && <span data-testid="inbox-unread-badge" className="absolute -right-1 -top-1 bg-amber-500 text-slate-950 text-[10px] font-bold min-w-4 px-1 rounded-full text-center">{unread > 99 ? '99+' : unread}</span>}
  </Link>;
};

export const UserName = ({ userId, children, testId, className = '' }) => {
  const { openMember } = useMessaging();
  const { user } = useAuth();
  const navigate = useNavigate();
  if (!userId) return <span className={className}>{children}</span>;
  return <button type="button" data-testid={testId || `member-card-trigger-${userId}`} onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (user) openMember(userId); else navigate('/registro'); }} className={`text-left hover:text-amber-400 transition-colors sc-focus rounded-sm ${className}`}>{children}</button>;
};
